import { useLayoutEffect, useState, useSyncExternalStore } from 'react';

export interface WorkspaceHistorySnapshot {
  dirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  revision: number;
}
export interface WorkspaceHistory {
  readonly dirty: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  undo: () => void;
  redo: () => void;
  checkpoint: (value?: never) => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => WorkspaceHistorySnapshot;
}
export interface WorkspaceEditHistory<T> extends Omit<WorkspaceHistory, 'checkpoint'> {
  checkpoint: (value?: T) => void;
}

/** Files are immutable handles; copying a draft never reads or duplicates bytes. */
export function copyWorkspaceValue<T>(value: T, seen = new Map<object, unknown>()): T {
  if (!value || typeof value !== 'object') return value;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
  if (value instanceof Date) return new Date(value.getTime()) as T;
  if (seen.has(value)) return seen.get(value) as T;
  if (value instanceof Set) {
    const result = new Set(); seen.set(value, result);
    value.forEach(item => result.add(copyWorkspaceValue(item, seen))); return result as T;
  }
  if (value instanceof Map) {
    const result = new Map(); seen.set(value, result);
    value.forEach((item, key) => result.set(copyWorkspaceValue(key, seen), copyWorkspaceValue(item, seen))); return result as T;
  }
  const result = (Array.isArray(value) ? [] : {}) as Record<string, unknown>;
  seen.set(value, result);
  for (const key of Object.keys(value)) result[key] = copyWorkspaceValue((value as Record<string, unknown>)[key], seen);
  return result as T;
}

export function equalWorkspaceValue(a: unknown, b: unknown, seen = new Map<object, object>()): boolean {
  if (Object.is(a, b)) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (a.constructor !== b.constructor) return false;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (typeof Blob !== 'undefined' && (a instanceof Blob || b instanceof Blob)) return false;
  if (seen.get(a) === b) return true;
  seen.set(a, b);
  if (a instanceof Set && b instanceof Set) {
    return a.size === b.size && [...a].every(item => [...b].some(other => equalWorkspaceValue(item, other)));
  }
  if (a instanceof Map && b instanceof Map) {
    return a.size === b.size && [...a].every(([key, value]) => b.has(key) && equalWorkspaceValue(value, b.get(key), seen));
  }
  const left = Object.keys(a); const right = Object.keys(b);
  return left.length === right.length && left.every(key => Object.hasOwn(b, key)
    && equalWorkspaceValue((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key], seen));
}

function valueBytes(value: unknown, seen = new Set<object>()): number {
  if (typeof value === 'string') return value.length * 2;
  if (!value || typeof value !== 'object') return 8;
  if (seen.has(value) || typeof Blob !== 'undefined' && value instanceof Blob) return 16;
  seen.add(value);
  if (value instanceof Map) return [...value].reduce((sum, [key, item]) => sum + valueBytes(key, seen) + valueBytes(item, seen), 0);
  if (value instanceof Set) return [...value].reduce((sum, item) => sum + valueBytes(item, seen), 0);
  return Object.entries(value).reduce((sum, [key, item]) => sum + key.length * 2 + valueBytes(item, seen), 0);
}

/** Only a continuous single-character edit of the same text field is grouped. */
function typingPath(a: unknown, b: unknown, path = ''): string | null {
  if (equalWorkspaceValue(a, b)) return null;
  if (typeof a === 'string' && typeof b === 'string') {
    let prefix = 0; let suffix = 0;
    while (prefix < Math.min(a.length, b.length) && a[prefix] === b[prefix]) prefix++;
    while (suffix < Math.min(a.length, b.length) - prefix && a[a.length - 1 - suffix] === b[b.length - 1 - suffix]) suffix++;
    return a.length - prefix - suffix <= 1 && b.length - prefix - suffix <= 1 ? path : null;
  }
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) || Array.isArray(b)
    || a instanceof Date || b instanceof Date || a instanceof Set || b instanceof Set || a instanceof Map || b instanceof Map) return null;
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].filter(key => !equalWorkspaceValue(
    (a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
  return keys.length === 1 ? typingPath((a as Record<string, unknown>)[keys[0]], (b as Record<string, unknown>)[keys[0]], `${path}.${keys[0]}`) : null;
}

/** A feature-owned local history. It has no access to mutations or router state. */
export function createWorkspaceEditHistory<T>(initial: T, restore: (value: T) => void, initialBaseline = initial) {
  let current = copyWorkspaceValue(initial);
  let baseline = copyWorkspaceValue(initialBaseline);
  let past: T[] = [];
  let future: T[] = [];
  let pending = false;
  let disabled = false;
  let revision = 0;
  let group: { path: string; time: number } | null = null;
  const listeners = new Set<() => void>();
  let snapshot: WorkspaceHistorySnapshot = { dirty: !equalWorkspaceValue(current, baseline), canUndo: false, canRedo: false, revision };
  const publish = () => {
    snapshot = { dirty: !equalWorkspaceValue(current, baseline), canUndo: !pending && !disabled && past.length > 0,
      canRedo: !pending && !disabled && future.length > 0, revision: ++revision };
    listeners.forEach(listener => listener());
  };
  const limit = () => {
    // Large manifest fields and long editing sessions have a fixed memory budget.
    while (past.length > 100 || past.length > 1 && past.reduce((size, item) => size + valueBytes(item), 0) > 8 * 1024 * 1024) past.shift();
  };
  const checkpoint = (value = current) => {
    current = copyWorkspaceValue(value); baseline = copyWorkspaceValue(value);
    past = []; future = []; group = null; publish();
  };
  const history: WorkspaceEditHistory<T> = {
    get dirty() { return snapshot.dirty; }, get canUndo() { return snapshot.canUndo; }, get canRedo() { return snapshot.canRedo; },
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    getSnapshot: () => snapshot,
    checkpoint,
    undo() {
      if (!history.canUndo) return;
      const next = past.pop()!; future.push(current); current = next; group = null;
      restore(copyWorkspaceValue(current)); publish();
    },
    redo() {
      if (!history.canRedo) return;
      const next = future.pop()!; past.push(current); current = next; group = null;
      restore(copyWorkspaceValue(current)); publish();
    },
  };
  return Object.assign(history, {
    record(value: T) {
      if (equalWorkspaceValue(value, current)) return;
      if (disabled) { current = copyWorkspaceValue(value); past = []; future = []; group = null; publish(); return; }
      const path = typingPath(current, value); const now = Date.now();
      if (!path || !group || group.path !== path || now - group.time > 600 || future.length) past.push(current);
      current = copyWorkspaceValue(value); future = []; group = path ? { path, time: now } : null;
      limit(); publish();
    },
    rebase(value: T, nextBaseline: T) {
      current = copyWorkspaceValue(value); baseline = copyWorkspaceValue(nextBaseline);
      past = disabled || equalWorkspaceValue(current, baseline) ? [] : [copyWorkspaceValue(baseline)]; future = []; group = null; publish();
    },
    lock(nextPending: boolean, nextDisabled: boolean) {
      if (pending === nextPending && disabled === nextDisabled) return;
      pending = nextPending; disabled = nextDisabled;
      if (disabled) { past = []; future = []; group = null; }
      publish();
    },
  });
}

export interface WorkspaceEditHistoryOptions<T> {
  value: T;
  baseline?: T;
  onRestore: (value: T) => void;
  identity?: string | number | boolean;
  pending?: boolean;
  disabled?: boolean;
}

function createHistoryBinding<T>(initial: WorkspaceEditHistoryOptions<T>) {
  let restore = initial.onRestore;
  let identity = initial.identity;
  let baseline = copyWorkspaceValue(initial.baseline);
  const history = createWorkspaceEditHistory(initial.value, (value: T) => restore(value), initial.baseline ?? initial.value);
  return { history, sync(options: WorkspaceEditHistoryOptions<T>) {
    restore = options.onRestore;
    history.lock(Boolean(options.pending), Boolean(options.disabled));
    if (identity !== options.identity) {
      identity = options.identity; baseline = copyWorkspaceValue(options.baseline);
      history.rebase(options.value, options.baseline ?? options.value);
    } else if (options.baseline !== undefined && !equalWorkspaceValue(baseline, options.baseline)) {
      baseline = copyWorkspaceValue(options.baseline); history.rebase(options.value, options.baseline);
    } else history.record(options.value);
  } };
}

/** Synchronize the feature's value with its external editing history. */
export function useWorkspaceEditHistory<T>({ value, baseline, onRestore, identity, pending = false, disabled = false }: WorkspaceEditHistoryOptions<T>): WorkspaceEditHistory<T> {
  const [binding] = useState(() => createHistoryBinding({ value, baseline, onRestore, identity, pending, disabled }));
  useLayoutEffect(() => {
    binding.sync({ value, baseline, onRestore, identity, pending, disabled });
  }, [binding, value, baseline, identity, pending, disabled, onRestore]);
  useSyncExternalStore(binding.history.subscribe, binding.history.getSnapshot, binding.history.getSnapshot);
  return binding.history;
}
